import React, { useEffect, useState } from 'react';

import { 
  GraduationCap, BookOpen, CheckCircle, 
  Lightbulb, ShieldCheck, Zap, 
  Users, Award, ChevronRight 
} from 'lucide-react';
import SEO from '../seo/SEO';

const SkillUpgradationPage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <SEO
        title="Skill Development & Training | GlobalEPRC"
        description="GlobalEPRC delivers work-ready talent through structured skill development and training programs, certifying candidates against industry standards."
        path="/skill-upgradation"
      />

      {/* 1. HERO SECTION */}
      <section className="pt-48 pb-20 bg-slate-50 relative overflow-hidden">
        {/* Abstract background shapes */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-amber-500/5 -skew-x-12 translate-x-24"></div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-1/2">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-1.5 rounded-full mb-8 font-bold text-xs uppercase tracking-[0.2em]">
                <Award className="h-4 w-4" /> Professional Excellence
              </div>
              <h1 className="text-5xl md:text-7xl font-black text-slate-900 mb-8 leading-tight">
                Skill <br />
                <span className="text-gold-gradient">Upgradation</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed mb-8 border-l-4 border-amber-500 pl-6">
                GlobalEPRC delivers more than human resources—we deliver work-ready talent. Every candidate is trained and certified through structured skill development programs aligned with industry standards.
              </p>
            </div>
            <div className="lg:w-1/2 relative">
               <div className="absolute -inset-4 bg-gold-gradient rounded-[3rem] blur opacity-10"></div>
               <img 
                 src="./skill4.jpeg" 
                 alt="Professional Training Session" 
                 className="relative rounded-[2.5rem] shadow-2xl border border-white"
               />
            </div>
          </div>
        </div>
      </section>

      {/* 2. TRAINING MODULES */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Soft Skills Card */}
            <div className="group p-8 md:p-12 rounded-[3rem] bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-2xl transition-all duration-500 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                <Users className="h-32 w-32 text-amber-900" />
              </div>
              <div className="bg-white p-5 rounded-2xl shadow-sm group-hover:bg-gold-gradient group-hover:text-white transition-all w-fit mb-8">
                <Lightbulb className="h-8 w-8 text-amber-600 group-hover:text-white" />
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-6">Soft Skills Training</h3>
              <p className="text-slate-600 leading-relaxed mb-8 text-lg">
                Focuses on housekeeping courses, workplace communication, professional etiquette, grooming standards and behavioral readiness—ensuring candidates integrate smoothly into client work environments.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <img src="./skill2.png" className="rounded-2xl shadow-md" alt="Communication Training" />
                <img src="./skill3.jpeg" className="rounded-2xl shadow-md mt-4" alt="Behavioral readiness" />
              </div>
            </div>

            {/* Technical Skills Card */}
            <div className="group p-8 md:p-12 rounded-[3rem] bg-slate-900 border border-slate-800 hover:shadow-2xl transition-all duration-500 relative overflow-hidden text-white">
              <div className="absolute top-0 right-0 p-8 opacity-10">
                <Zap className="h-32 w-32 text-amber-400" />
              </div>
              <div className="bg-white/10 p-5 rounded-2xl backdrop-blur-md group-hover:bg-gold-gradient group-hover:text-white transition-all w-fit mb-8 border border-white/10">
                <BookOpen className="h-8 w-8 text-amber-400 group-hover:text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-6">Technical Skills Training</h3>
              <p className="text-slate-300 leading-relaxed mb-8 text-lg">
                Role-specific and trade-based, covering practical job skills, safety standards, operational procedures, and technical certification required for on-site performance.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <img src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&q=80&w=400" className="rounded-2xl shadow-md grayscale group-hover:grayscale-0 transition-all duration-500" alt="Technical Skills" />
                <img src="./skill1.jpeg" className="rounded-2xl shadow-md mt-4 grayscale group-hover:grayscale-0 transition-all duration-500" alt="Operational Procedures" />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 3. CLIENT BENEFITS SECTION */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-black text-slate-900 mb-6">What This Means For Our Clients</h2>
            <div className="h-1.5 w-24 bg-gold-gradient mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                title: "Faster Onboarding", 
                icon: Zap, 
                desc: "Workforce that meets operational expectations from day one." 
              },
              { 
                title: "Improved Efficiency", 
                icon: Lightbulb, 
                desc: "Ensuring candidates integrate smoothly into client work environments." 
              },
              { 
                title: "Certified Safety", 
                icon: ShieldCheck, 
                desc: "Every candidate is trained through structured programs aligned with safety standards." 
              }
            ].map((benefit, i) => (
              <div key={i} className="bg-white p-10 rounded-3xl shadow-sm border border-slate-100 hover:border-amber-200 transition-all text-center">
                <div className="bg-amber-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="h-8 w-8 text-amber-600" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-4">{benefit.title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-20 p-10 bg-white rounded-[2.5rem] shadow-xl border border-slate-100 flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/3">
              <img src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&q=80&w=500" className="rounded-2xl shadow-lg" alt="Quality expectations" />
            </div>
            <div className="md:w-2/3">
              <p className="text-2xl font-bold text-slate-800 leading-snug">
                "Our candidates meet operational, safety, and quality expectations from day one."
              </p>
              <button className="mt-8 bg-gold-gradient text-white px-8 py-4 rounded-xl font-bold flex items-center gap-2 hover:scale-105 transition-transform shadow-lg">
                View Training Programs <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default SkillUpgradationPage;